import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FileThumbnailProps {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

const FileThumbnail: React.FC<FileThumbnailProps> = ({
  file,
  onRemove,
  disabled = false
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file]);


  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative inline-block group">
            {/* Miniatura */}
            <div className="w-24 h-24 rounded-lg overflow-hidden border border-border bg-muted shadow-sm cursor-pointer">
              {file.type.startsWith('image/') && imagePreview ? (
                <img
                  src={imagePreview}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src="/tumb_doc.png"
                  alt="Document thumbnail"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            {/* Botão X - sempre visível no canto superior direito */}
            <Button
              variant="secondary"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-md"
              onClick={onRemove}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </TooltipTrigger>
        
        {/* Tooltip com informações completas */}
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium text-sm break-words">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FileThumbnail;